import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { storage } from '../lib/storage';
import { purgeTenantData } from '../lib/tenant-cleanup';
import { classifyAuthFailure, MalformedProfileError } from '../lib/auth-failure';
import { forgetPushDevice } from '../lib/push';
import { setAppBadgeCount } from '../lib/app-badge';
import { useSyncStore } from '../lib/offline/sync-store';
import { onPublicTokenPage } from '../lib/public-routes';

interface AuthContextType {
  user: any | null;
  company: any | null;
  isAuthenticated: boolean;
  isFieldWorker: boolean;
  isCustomer: boolean;
  isLoading: boolean;
  /**
   * This session was restored from the device, not confirmed by the server.
   *
   * Everything on screen is as fresh as the last time the app had signal, and
   * the token has not been re-checked — so a write may still be refused when
   * coverage returns. Surfaced by `SyncStatusBar`; do not use it as a
   * permission gate, the server remains the authority on every request.
   */
  sessionFromCache: boolean;
  hasVertical: (vertical: string) => boolean;
  updateCompany: (patch: Record<string, any>) => void;
  login: (email: string, password: string) => Promise<{ user: any; company: any | null }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any | null>(null);
  const [company, setCompany] = useState<any | null>(null);
  const [isFieldWorker, setIsFieldWorker] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  /**
   * Held in the sync store, not in this component's state.
   *
   * `SyncStatusBar` is the thing that renders it and must not import this
   * context — see the note on `SyncState.sessionFromCache`. Keeping one value
   * in one place is why the bar and `useAuth()` cannot disagree about whether
   * the session was confirmed.
   */
  const sessionFromCache = useSyncStore((s) => s.sessionFromCache);
  const setSessionFromCache = useSyncStore((s) => s.setSessionFromCache);

  /**
   * Seat a profile into state. One place, because the four setters have to move
   * together — a role read from one source and a company from another is how a
   * hand gets the wrong route tree.
   */
  const applyProfile = useCallback((nextUser: any, nextCompany: any | null) => {
    setUser(nextUser);
    setCompany(nextCompany ?? null);
    setIsFieldWorker(nextUser?.role === 'field_worker' || nextUser?.role === 'driver');
    setIsCustomer(nextUser?.role === 'customer');
  }, []);

  /**
   * Ask the server who we are, and keep the answer for the next dead zone.
   *
   * Throws `MalformedProfileError` on a 200 that carries no user, so the caller
   * treats it as "did not learn anything" rather than as a confirmed session
   * with `undefined` for a role.
   */
  const confirmSession = useCallback(async () => {
    const response = await api.auth.me();
    const meUser = response.data?.user;
    const meCompany = response.data?.company ?? null;
    if (!meUser) throw new MalformedProfileError();
    applyProfile(meUser, meCompany);
    setSessionFromCache(false);
    await storage.setCachedSession(meUser, meCompany);
    return { user: meUser, company: meCompany };
  }, [applyProfile, setSessionFromCache]);

  const logout = useCallback(async () => {
    // Forget this device for push BEFORE the token goes — the DELETE needs it —
    // so the next person to sign in here does not get this person's alerts.
    await forgetPushDevice();
    // The badge is this person's unread count; it must not outlive them here.
    await setAppBadgeCount(0);
    await storage.removeToken();
    await storage.removeCachedSession();
    setUser(null);
    setCompany(null);
    setIsFieldWorker(false);
    setIsCustomer(false);
    setSessionFromCache(false);
    // Purge all tenant-scoped caches so the next login starts clean
    await purgeTenantData(queryClient);
  }, [queryClient, setSessionFromCache]);

  /**
   * Decide whether this device still has a session, WITHOUT destroying work to
   * do it.
   *
   * The rule is in `lib/auth-failure.ts`: only the server saying 401/403 ends
   * the session. Everything else — no answer, a timeout, a 5xx, a body that is
   * not a profile — leaves the token, the cached profile and IndexedDB exactly
   * where they are, and the hand carries on against what the device already
   * holds. A read may degrade; a queue may not be deleted.
   *
   * The no-cached-profile case is the one worth being explicit about: we still
   * do not purge. The hand lands on the sign-in screen because there is nothing
   * to show them, but whatever is queued survives to be pushed after they sign
   * in on the far side of the dead zone.
   */
  const checkAuth = useCallback(async () => {
    // A customer's magic link (lib/public-routes.ts) is not a reason to confirm
    // or end whatever staff session this browser holds. Probing it here is what
    // sent a stale-token browser to /login on the first click of a review link
    // — and signed the employee out for opening it. Leave the session as found.
    if (onPublicTokenPage()) {
      setIsLoading(false);
      return;
    }
    const token = await storage.getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    try {
      await confirmSession();
    } catch (error) {
      if (classifyAuthFailure(error) === 'rejected') {
        await logout();
      } else {
        const cached = await storage.getCachedSession();
        if (cached) {
          applyProfile(cached.user, cached.company);
          setSessionFromCache(true);
          console.info(
            '[auth] /auth/me did not answer; opened from the profile saved on this device. ' +
            'Token, cached data and the sync queue are untouched.'
          );
        } else {
          console.info(
            '[auth] /auth/me did not answer and this device has no saved profile. ' +
            'Signing in is needed, but nothing has been purged.'
          );
        }
      }
    }
    setIsLoading(false);
  }, [applyProfile, confirmSession, logout, setSessionFromCache]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { checkAuth(); }, []);

  /**
   * When coverage comes back, ask the server again — but only ever to UPGRADE.
   *
   * A successful re-check swaps the cached profile for a live one and clears
   * the banner. A failure of any kind, INCLUDING a 401, changes nothing here.
   *
   * That last part is deliberate and is not the boot rule. At boot nothing is
   * in flight. Here the hand has just driven back into coverage and the sync
   * engine is pushing work whose only copy is `syncQueue` — purging it to
   * enforce a session rule would delete the tickets moments before the push
   * that was about to save them. A genuinely dead token is still caught: the
   * push gets its own 401, and the response interceptor in `api/client.ts`
   * drops the token and sends the user to /login without purging, which is
   * already how a mid-session expiry behaves for every other endpoint.
   */
  useEffect(() => {
    // Nothing to upgrade unless we are running on a saved profile.
    if (!sessionFromCache) return;
    return useSyncStore.subscribe((state, prev) => {
      if (!state.isOnline || prev.isOnline) return;
      void confirmSession().catch(() => {
        /* Still no usable answer. Stay on the saved profile, keep the banner. */
      });
    });
  }, [sessionFromCache, confirmSession]);

  const login = async (email: string, password: string): Promise<{ user: any; company: any | null }> => {
    // Belt-and-suspenders: purge any residual tenant data before starting a new session
    // (covers tab-close, token expiry, or direct navigation bypassing logout)
    await purgeTenantData(queryClient);

    const response = await api.auth.login(email, password);
    const token = response.data?.token;
    const loginUser = response.data?.user;

    if (!token) {
      throw new Error('Login succeeded but no token returned');
    }

    await storage.setToken(token);

    // Fetch full profile (incl. company) via /auth/me
    try {
      return await confirmSession();
    } catch (meError: any) {
      // If /me fails, proceed with login response data
      console.error('/auth/me failed:', meError);
      if (loginUser) {
        applyProfile(loginUser, null);
        setSessionFromCache(false);
        // Deliberately NOT cached. This profile came from the login response,
        // which carries no company — saving it would hand the next offline boot
        // a session with no verticals, and `hasVertical` returns false for
        // every one of them. Better to have no cached session than a crippled
        // one; the next successful /auth/me writes a whole profile.
        return { user: loginUser, company: null };
      } else {
        throw meError;
      }
    }
  };

  const hasVertical = (vertical: string): boolean => {
    if (!company) return false;
    const verticals: string[] = company.enabledVerticals ?? company.enabled_verticals ?? [];
    return verticals.includes(vertical);
  };

  const updateCompany = (patch: Record<string, any>) => {
    setCompany((prev: any) => prev ? { ...prev, ...patch } : prev);
  };

  return (
    <AuthContext.Provider value={{
      user,
      company,
      isAuthenticated: !!user,
      isFieldWorker,
      isCustomer,
      isLoading,
      sessionFromCache,
      hasVertical,
      updateCompany,
      login,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

/**
 * The session when there is one, `undefined` outside an AuthProvider. For a
 * component that only TAILORS itself to the tenant (the JSA form's default
 * steps) and must still render bare — in its own tests, for one. Anything that
 * needs a session to be correct uses `useAuth`, which throws.
 */
export const useOptionalAuth = () => useContext(AuthContext);
